import React from 'react'

import { RelatedContactFragment } from 'src/graphql'
import { useState } from 'src/hooks'

import SectionTitle from '../SectionTitle'
import {
  ContactAddress,
  ContactDates,
  ContactNames,
  ContactNumbers,
} from '../details/DetailViews'
import styles from './contactDetails.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'

type Props<T extends RelatedContactFragment> = {
  contact: T
  isExpanded: boolean
  isExpandingAllowed: boolean
  onDelete?: (id: string) => void
  onGoToContact?: (id: string) => void
  onEdit: () => void
  contactCategory?: string
}

const ContactDetails = <T extends RelatedContactFragment>(props: Props<T>) => {
  const {
    contact,
    isExpandingAllowed,
    onDelete,
    onGoToContact,
    onEdit,
    contactCategory,
  } = props

  const [isExpanded, setIsExpanded] = useState(props.isExpanded)

  if (!isExpanded) {
    return (
      <section
        id={contactCategory === 'spouse' ? 'spouse_contact_details' : undefined}
      >
        <ContactNames weight="bold" {...contact} />
        <ContactDates
          birthday={contact.birthday ?? undefined}
          anniversary={contact.anniversary ?? undefined}
        />
        <div className={styles.actions}>
          {isExpandingAllowed && (
            <span onClick={() => setIsExpanded(x => !x)}>
              <Text type="caption" color="primaryBrand">
                See More Info
              </Text>
            </span>
          )}
          {onEdit && (
            <span
              onClick={() => onEdit()}
              id={contactCategory === 'spouse' ? 'edit_spouse_btn' : undefined}
            >
              <Text type="caption" color="primaryBrand">
                Edit
              </Text>
            </span>
          )}
          {onGoToContact && (
            <span onClick={() => onGoToContact(contact.id)}>
              <Text type="caption" color="primaryBrand">
                Go To
              </Text>
            </span>
          )}
          {onDelete && (
            <span
              onClick={() => onDelete(contact.id)}
              id={
                contactCategory === 'spouse' ? 'delete_spouse_btn' : undefined
              }
            >
              <Text type="caption" color="primaryBrand">
                Delete
              </Text>
            </span>
          )}
        </div>
      </section>
    )
  }

  return (
    <div style={{ marginBottom: 25 }}>
      <div>
        <section>
          <SectionTitle
            title={'Name'}
            onEdit={onEdit}
            id={'edit_contact_name_btn'}
          />
          <ContactNames {...contact} />
        </section>

        <section>
          <SectionTitle
            title={'Address'}
            onEdit={onEdit}
            id="edit_contact_address_btn"
          />
          <ContactAddress {...contact} />
        </section>

        <section>
          <SectionTitle title={'Contact'} onEdit={onEdit} />
          <ContactNumbers {...contact} />
        </section>

        <section>
          <SectionTitle title={'Important Dates'} onEdit={onEdit} />
          <ContactDates
            birthday={contact.birthday ?? undefined}
            anniversary={contact.anniversary ?? undefined}
          />
        </section>

        <div className={styles.actions}>
          {isExpandingAllowed && (
            <span onClick={() => setIsExpanded(false)}>
              <Text type="caption" color="primaryBrand">
                See Less Info
              </Text>
            </span>
          )}
          {onGoToContact && (
            <span onClick={() => onGoToContact(contact.id)}>
              <Text type="caption" color="primaryBrand">
                Go To
              </Text>
            </span>
          )}
          {onDelete && <span onClick={() => onDelete(contact.id)}>Delete</span>}
        </div>
      </div>
    </div>
  )
}

export default ContactDetails
